/**
 * User List Component
 *
 * Renders the users table on the design system's `DataTable`, which is the
 * mandated list surface: it draws its own container, its own loading skeletons
 * and its own empty state, so it must NOT be wrapped in a Card (that would
 * double the border).
 */

import React from 'react';
import {
  DataTable,
  EmptyState,
  Pagination,
  Badge,
  type DataTableColumn,
} from '@lognai/synapse-design-system-v2';
import { Users } from '@lognai/synapse-design-system-v2/icons';
import type { User } from '@lognai/core-platform';
import { UserStatus } from '../../types/domain-enums';
import { UserActionsMenu, type UserActionCallbacks } from './UserActionsMenu';

/**
 * What the list endpoint actually returns for a row.
 *
 * `User.roles` is a legacy string array ('admin', 'user', 'manager') that
 * predates the company role catalogue and corresponds to nothing in it — the
 * Roles column rendered it as a badge, so an admin auditing access saw a role
 * the Manage Roles screen would then say the user does not have (QA ledger
 * ADM-001). The list now also carries the real assignments.
 */
type UserRow = User & {
  assignedRoles?: Array<{ id: string; name: string }>;
  directPermissionCount?: number;
};

export interface UserListProps {
  users: User[];
  isLoading?: boolean;
  pagination?: { page: number; limit: number; total: number; totalPages: number };
  onPageChange?: (page: number) => void;
  onLimitChange?: (limit: number) => void;
  /**
   * Whether a search term or filter is narrowing the list. Changes the empty
   * state from "there are no users" to "nothing matched".
   */
  hasActiveFilters?: boolean;
  onEditUser?: (user: User) => void;
  onResetPassword?: (user: User) => void;
  onManageRoles?: (user: User) => void;
  onManageAccess?: (user: User) => void;
  onActivateUser?: (user: User) => void;
  onSuspendUser?: (user: User) => void;
  onDeleteUser?: (user: User) => void;
}

const getStatusVariant = (
  status: UserStatus
): 'default' | 'secondary' | 'destructive' | 'outline' => {
  switch (status) {
    case 'active':
      return 'default';
    case 'inactive':
      return 'secondary';
    case 'suspended':
      return 'destructive';
    case 'pending':
      return 'outline';
    default:
      return 'default';
  }
};

/** Row actions, kept out of the column list so the column array stays readable. */
type UserActions = Omit<UserActionCallbacks, 'onRemoveFromCompany'>;

/**
 * Built as a function of the row actions, so the page can reuse the same
 * definitions for a ColumnSettingsMenu without duplicating them.
 */
export const buildUserColumns = (actions: UserActions): DataTableColumn<User>[] => [
  {
    id: 'user',
    header: 'User',
    cell: (user) => (
      <>
        <div className="font-medium">{user.fullName || user.email}</div>
        {user.fullName && <div className="text-sm text-muted-foreground">{user.email}</div>}
      </>
    ),
  },
  {
    id: 'department',
    header: 'Department / Title',
    cell: (user) =>
      user.department || user.jobTitle ? (
        <>
          {user.department && <div>{user.department}</div>}
          {user.jobTitle && <div className="text-sm text-muted-foreground">{user.jobTitle}</div>}
        </>
      ) : (
        <div className="italic text-muted-foreground">Not set</div>
      ),
  },
  {
    id: 'employeeId',
    header: 'Employee ID',
    cell: (user) =>
      user.employeeId ? (
        <span className="font-mono text-sm">{user.employeeId}</span>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    id: 'status',
    header: 'Status',
    cell: (user) => (
      <Badge variant={getStatusVariant(user.status)}>
        {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
      </Badge>
    ),
  },
  {
    id: 'roles',
    header: 'Roles',
    cell: (user) => {
      const assigned = (user as UserRow).assignedRoles;
      // Absent means the lookup failed — not that the user has no roles.
      if (assigned === undefined) {
        return (
          <span
            className="italic text-muted-foreground"
            title="Role assignments could not be loaded for this page. This is not a claim that the user has none."
          >
            Unknown
          </span>
        );
      }
      if (assigned.length > 0) {
        return (
          <div className="flex flex-wrap gap-1">
            {assigned.slice(0, 2).map((role) => (
              <Badge key={role.id} variant="outline" className="text-xs">
                {role.name}
              </Badge>
            ))}
            {assigned.length > 2 && (
              <Badge variant="outline" className="text-xs">
                +{assigned.length - 2}
              </Badge>
            )}
          </div>
        );
      }
      /*
        No role, but possibly access. Saying only "No roles" next to a user who
        can plainly do things is as misleading as the badge this replaced — but
        the first attempt said "No roles · 12 direct", which under a column
        headed "Roles" reads as a contradiction rather than as two facts
        (QA ledger ADM2-001).

        So the case is NAMED instead of negated. "Direct access only" is the
        same information — this user's access does not come from a role — in a
        form nobody has to reconcile, and the count keeps it actionable.
      */
      const direct = (user as UserRow).directPermissionCount ?? 0;
      return (
        <span
          className="italic text-muted-foreground"
          title={
            direct > 0
              ? `This user holds no role assignment. Their access comes from ${direct} permission${direct === 1 ? '' : 's'} granted directly — see Manage Access.`
              : 'This user holds no role assignments and no direct permissions.'
          }
        >
          {direct > 0 ? `Direct access only · ${direct}` : 'No roles'}
        </span>
      );
    },
  },
  {
    id: 'actions',
    header: '',
    align: 'right',
    // Right-aligned but not figures — opt out of tabular numerals.
    numeric: false,
    hideable: false,
    menuLabel: 'Actions',
    className: 'w-[50px]',
    cell: (user) => <UserActionsMenu user={user} {...actions} />,
  },
];

export const UserList: React.FC<UserListProps> = ({
  users,
  isLoading = false,
  pagination,
  onPageChange,
  onLimitChange,
  hasActiveFilters = false,
  onEditUser,
  onResetPassword,
  onManageRoles,
  onManageAccess,
  onActivateUser,
  onSuspendUser,
  onDeleteUser,
}) => {
  const columns = React.useMemo(
    () =>
      buildUserColumns({
        onEditUser,
        onResetPassword,
        onManageRoles,
        onManageAccess,
        onActivateUser,
        onSuspendUser,
        onDeleteUser,
      }),
    [onEditUser, onResetPassword, onManageRoles, onManageAccess, onActivateUser, onSuspendUser, onDeleteUser]
  );

  const emptyMessage = (
    <EmptyState
      icon={<Users />}
      title={hasActiveFilters ? 'No users match your filters' : 'No users yet'}
      description={
        hasActiveFilters
          ? 'Try a different search term, or clear the filters to see every user.'
          : 'Invite a user to get started.'
      }
      compact
    />
  );

  return (
    <DataTable
      data={users}
      columns={columns}
      rowKey={(user) => user.id}
      isLoading={isLoading}
      emptyMessage={emptyMessage}
      // The pager stays mounted through every state. Unmounting it on an empty
      // page — which server-side filtering makes reachable — would strand the
      // user on a page they cannot navigate away from.
      footer={
        pagination && onPageChange && onLimitChange ? (
          <Pagination
            variant="controls"
            pagination={pagination}
            onPageChange={onPageChange}
            onLimitChange={onLimitChange}
            isLoading={isLoading}
          />
        ) : undefined
      }
    />
  );
};
